import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';

const AdminSettings = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('system');
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState(null);
  
  // Email configuration state
  const [emailConfig, setEmailConfig] = useState({
    mail_server: '',
    mail_port: 587,
    mail_username: '',
    mail_password: '',
    mail_from: '',
    mail_from_name: 'Lucifer Platform',
    mail_starttls: true,
    mail_ssl_tls: false,
    use_credentials: true,
    validate_certs: true,
    platform_url: 'http://localhost:3000',
    platform_name: 'Lucifer'
  });
  
  const [emailStatus, setEmailStatus] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Template editor state
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [editorMode, setEditorMode] = useState('edit'); // 'edit' or 'preview'
  
  // Test email state
  const [testEmail, setTestEmail] = useState({
    recipient_email: '',
    test_type: 'connection',
    template_type: '',
    language: 'ca'  // el català és l'idioma de l'aplicació
  });
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (currentUser?.role !== 'administrator') {
      return;
    }
    fetchAllData();
  }, [currentUser]);

  const fetchAllData = async () => {
    try {
      const [configData, statusData, templatesData, systemData] = await Promise.all([
        adminService.getEmailConfig(),
        adminService.getEmailStatus(),
        adminService.getEmailTemplates(),
        adminService.getSystemInfo()
      ]);
      
      setEmailConfig(configData);
      setEmailStatus(statusData);
      setEmailTemplates(templatesData.templates || []);
      setSystemInfo(systemData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEmailConfig = async () => {
    try {
      const result = await adminService.updateEmailConfig(emailConfig);
      toast.success('Email configuration validated successfully');
      toast.info(result.note);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.recipient_email) {
      toast.error('Please enter a recipient email address');
      return;
    }

    setSendingTest(true);
    try {
      await adminService.testEmail(testEmail);
      toast.success('Test email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleViewTemplate = async (templateName, language) => {
    try {
      const response = await adminService.getTemplateContent(templateName, language);
      setSelectedTemplate({
        name: templateName,
        language: language,
        description: getTemplateDescription(templateName)
      });
      setTemplateContent(response.content);
      setShowTemplateEditor(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load template');
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    setSavingTemplate(true);
    try {
      await adminService.updateTemplateContent(
        selectedTemplate.name,
        selectedTemplate.language,
        templateContent
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminSettings.templateUpdateFailed'));
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCloseEditor = () => {
    setShowTemplateEditor(false);
    setSelectedTemplate(null);
    setTemplateContent('');
    setEditorMode('edit');
  };

  const getTemplateDescription = (templateName) => {
    const descriptions = {
      'user_welcome': t('adminSettings.templateDescriptions.welcome'),
      'workspace_assignment': t('adminSettings.templateDescriptions.workspace_invite')
    };
    return descriptions[templateName] || templateName;
  };

  if (currentUser?.role !== 'administrator') {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">{t('common.accessDenied')}</h2>
          <p className="text-gray-600 mt-2">{t('adminSettings.accessRequired')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('adminSettings.title')}</h1>
        <p className="text-gray-600 mt-1">{t('adminSettings.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('system')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'system'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('adminSettings.systemInfo')}
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('adminSettings.emailConfig')}
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('adminSettings.templates')}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'system' && systemInfo && (
          <div className="space-y-6">
            {/* System Overview */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.systemOverview')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{systemInfo.users.total}</div>
                  <div className="text-sm text-gray-600">{t('adminSettings.totalUsers')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{systemInfo.users.active}</div>
                  <div className="text-sm text-gray-600">{t('adminSettings.activeUsers')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{systemInfo.workspaces.total}</div>
                  <div className="text-sm text-gray-600">{t('adminSettings.totalWorkspaces')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{systemInfo.workspaces.active}</div>
                  <div className="text-sm text-gray-600">{t('adminSettings.activeWorkspaces')}</div>
                </div>
              </div>
            </div>

            {/* Users by Role */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.usersByRole')}</h3>
              <div className="space-y-2">
                {systemInfo.users.by_role.map(item => (
                  <div key={item.role} className="flex justify-between items-center">
                    <span className="text-gray-700 capitalize">{t(`roles.${item.role}`)}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Users by Organization */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.usersByOrganization')}</h3>
              <div className="space-y-2">
                {systemInfo.users.by_organization.map(item => (
                  <div key={item.organization} className="flex justify-between items-center">
                    <span className="text-gray-700">{item.organization}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="space-y-6">
            {/* Email Status */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.emailServiceStatus')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${emailStatus.is_configured ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-sm">
                    {emailStatus.is_configured ? t('adminSettings.configured') : t('adminSettings.notConfigured')}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${emailStatus.configuration_complete ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                  <span className="text-sm">
                    {emailStatus.configuration_complete ? t('adminSettings.settingsComplete') : t('adminSettings.settingsIncomplete')}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${emailStatus.templates_available ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-sm">
                    {emailStatus.templates_available ? t('adminSettings.templatesAvailable') : t('adminSettings.templatesMissing')}
                  </span>
                </div>
              </div>
            </div>

            {/* Email Configuration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.smtpConfiguration')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.smtpServer')}
                  </label>
                  <input
                    type="text"
                    value={emailConfig.mail_server}
                    onChange={(e) => handleEmailConfigChange('mail_server', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.port')}
                  </label>
                  <input
                    type="number"
                    value={emailConfig.mail_port}
                    onChange={(e) => handleEmailConfigChange('mail_port', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.username')}
                  </label>
                  <input
                    type="text"
                    value={emailConfig.mail_username}
                    onChange={(e) => handleEmailConfigChange('mail_username', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    placeholder="your-email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.password')}
                  </label>
                  <input
                    type="password"
                    value={emailConfig.mail_password}
                    onChange={(e) => handleEmailConfigChange('mail_password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.fromEmail')}
                  </label>
                  <input
                    type="email"
                    value={emailConfig.mail_from}
                    onChange={(e) => handleEmailConfigChange('mail_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    placeholder="noreply@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.fromName')}
                  </label>
                  <input
                    type="text"
                    value={emailConfig.mail_from_name}
                    onChange={(e) => handleEmailConfigChange('mail_from_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={emailConfig.mail_starttls}
                    onChange={(e) => handleEmailConfigChange('mail_starttls', e.target.checked)}
                    className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{t('adminSettings.useStarttls')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={emailConfig.mail_ssl_tls}
                    onChange={(e) => handleEmailConfigChange('mail_ssl_tls', e.target.checked)}
                    className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{t('adminSettings.useSslTls')}</span>
                </label>
              </div>

              <div className="mt-6">
                <button
                  onClick={handleSaveEmailConfig}
                  className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors"
                >
                  {t('adminSettings.validateConfiguration')}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  {t('adminSettings.serverRestartNote')}
                </p>
              </div>
            </div>

            {/* Test Email */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.testEmail')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.recipientEmail')}
                  </label>
                  <input
                    type="email"
                    value={testEmail.recipient_email}
                    onChange={(e) => setTestEmail(prev => ({ ...prev, recipient_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    placeholder="test@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('adminSettings.testType')}
                  </label>
                  <select
                    value={testEmail.test_type}
                    onChange={(e) => setTestEmail(prev => ({ ...prev, test_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                  >
                    <option value="connection">{t('adminSettings.connectionTest')}</option>
                    <option value="template">{t('adminSettings.templateTest')}</option>
                  </select>
                </div>
                
                {testEmail.test_type === 'template' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('adminSettings.template')}
                      </label>
                      <select
                        value={testEmail.template_type}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, template_type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="">{t('adminSettings.selectTemplate')}</option>
                        {emailTemplates.map(template => (
                          <option key={template.name} value={template.name}>
                            {template.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('adminSettings.language')}
                      </label>
                      <select
                        value={testEmail.language}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="ca">Català</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              
              <div className="mt-4">
                <button
                  onClick={handleTestEmail}
                  disabled={sendingTest || !emailStatus.is_configured}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingTest ? t('adminSettings.sending') : t('adminSettings.sendTestEmail')}
                </button>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Email Templates Management */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('adminSettings.availableEmailTemplates')}</h3>
              <div className="space-y-4">
                {emailTemplates.map(template => (
                  <div key={template.name} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{getTemplateDescription(template.name)}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('adminSettings.languages')}: {template.languages.join(', ')}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {template.languages.length} {template.languages.length === 1 ? t('adminSettings.language') : t('adminSettings.languages')}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {template.languages.map(lang => (
                        <div key={lang} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-gray-900">
                              {lang === 'en' ? 'English' : lang === 'es' ? 'Español' : 'Català'}
                            </h5>
                            <span className="text-xs text-gray-500 uppercase">{lang}</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            Template file: {template.name}_{lang}.html
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewTemplate(template.name, lang)}
                              className="flex-1 px-3 py-1.5 text-xs bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
                            >
                              {t('adminSettings.viewTemplate')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {emailTemplates.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 border border-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V11" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">{t('adminSettings.templatesMissing')}</h3>
                    <p className="text-sm text-gray-500">No email templates found in the system.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template Editor Modal */}
      {showTemplateEditor && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('adminSettings.templateEditor')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTemplate.description} - {selectedTemplate.language === 'en' ? 'English' : selectedTemplate.language === 'es' ? 'Español' : 'Català'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Edit/Preview Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setEditorMode('edit')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      editorMode === 'edit'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {t('adminSettings.editMode')}
                  </button>
                  <button
                    onClick={() => setEditorMode('preview')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      editorMode === 'preview'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {t('adminSettings.previewMode')}
                  </button>
                </div>
                <button
                  onClick={handleCloseEditor}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-6 overflow-hidden">
              <div className="h-full flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editorMode === 'edit' ? t('adminSettings.templateContent') : t('adminSettings.previewMode')}
                </label>
                
                {editorMode === 'edit' ? (
                  <textarea
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    className="flex-1 w-full p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-accent-500 focus:border-accent-500"
                    placeholder="Template content..."
                    style={{ minHeight: '400px' }}
                  />
                ) : (
                  <div className="flex-1 w-full border border-gray-300 rounded-lg overflow-auto bg-white">
                    <iframe
                      srcDoc={templateContent}
                      className="w-full h-full border-0"
                      style={{ minHeight: '400px' }}
                      title="Template Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseEditor}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('adminSettings.closeEditor')}
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
              >
                {savingTemplate ? t('adminSettings.saving') : t('adminSettings.saveTemplate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;